import axios from 'axios';
import {useCallback, useEffect, useState} from 'react'

const responseCache = new Map();
const inflightRequests = new Map();
const cooldownUntil = new Map();

const CACHE_TTL_MS = 60 * 1000;
const PERSIST_TTL_MS = 10 * 60 * 1000;
const DEFAULT_COOLDOWN_MS = 30 * 1000;
const STORAGE_PREFIX = 'useFetchCache:';

const storageKey = (url) => `${STORAGE_PREFIX}${url}`;

export const invalidateFetchCache = (url) => {
    if(!url) return;
    responseCache.delete(url);
    inflightRequests.delete(url);
    cooldownUntil.delete(url);
    try{
        localStorage.removeItem(storageKey(url));
    }catch(_e){
    }
}

const readPersisted = (url) => {
    try{
        const raw = localStorage.getItem(storageKey(url));
        if(!raw) return null;
        const parsed = JSON.parse(raw);
        if(!parsed?.ts) return null;
        if(Date.now() - parsed.ts > PERSIST_TTL_MS) return null;
        return parsed.data ?? null;
    }catch(_e){
        return null;
    }
}

const writePersisted = (url,data) => {
    try{
        localStorage.setItem(storageKey(url), JSON.stringify({ ts: Date.now(), data }));
    }catch(_e){
    }
}

export const prefetchUrl = async (url) => {
    if(!url) return null;

    const now = Date.now();
    const cooldown = cooldownUntil.get(url);
    if (cooldown && now < cooldown) return null;

    const cached = responseCache.get(url);
    if (cached && now - cached.ts < CACHE_TTL_MS) return cached.data;

    const persisted = readPersisted(url);
    if (persisted) {
        responseCache.set(url, { data: persisted, ts: now });
        return persisted;
    }

    let requestPromise = inflightRequests.get(url);
    if (!requestPromise) {
        const token = localStorage.getItem('token');
        requestPromise = axios.get(url,{
            headers:{
                'Authorization': `Bearer ${token}` 
            }
        }).then((response) => {
            responseCache.set(url, { data: response.data, ts: Date.now() });
            writePersisted(url, response.data);
            return response.data;
        }).catch((err) => {
            const status = err?.response?.status;
            if (status === 429) {
                const retryAfter = Number(err?.response?.headers?.['retry-after']);
                const cooldownMs = Number.isFinite(retryAfter) && retryAfter > 0
                    ? retryAfter * 1000
                    : DEFAULT_COOLDOWN_MS;
                cooldownUntil.set(url, Date.now() + cooldownMs);
            }
            throw err;
        }).finally(() => {
            inflightRequests.delete(url);
        });
        inflightRequests.set(url, requestPromise);
    }
    return requestPromise;
}

const useFetch = (url, autoLoadOrOptions = true, maybeOptions) => {
    const parsedAutoLoad = typeof autoLoadOrOptions === 'boolean'
        ? autoLoadOrOptions
        : (autoLoadOrOptions?.autoLoad ?? true);
    const options = (autoLoadOrOptions && typeof autoLoadOrOptions === 'object' && !Array.isArray(autoLoadOrOptions))
        ? autoLoadOrOptions
        : (maybeOptions && typeof maybeOptions === 'object' && !Array.isArray(maybeOptions) ? maybeOptions : {});
    const useMemoryCache = options.cache !== false;
    const usePersistCache = options.persist !== false;

    const [data,setData] = useState(null);
    const [loading,setLoading] = useState(Boolean(parsedAutoLoad && url));
    const [error,setError]  = useState(null);
    const token = localStorage.getItem('token');

    const fetchData = useCallback(async ()=>{
        if(!url){
            setLoading(false);
            return;
        }
        const now = Date.now();
        const cooldown = cooldownUntil.get(url);
        if (cooldown && now < cooldown) {
            setLoading(false);
            setError(new Error('Too Many Attempts.'));
            return;
        }

        if (useMemoryCache) {
            const cached = responseCache.get(url);
            if (cached && now - cached.ts < CACHE_TTL_MS) {
                setData(cached.data);
                setError(null);
                setLoading(false);
                return cached.data;
            }
        }

        if (usePersistCache) {
            const persisted = readPersisted(url);
            if (persisted) {
                if (useMemoryCache) {
                    responseCache.set(url, { data: persisted, ts: now });
                }
                setData(persisted);
                setError(null);
                setLoading(false);
                return persisted;
            }
        }

        setLoading(true);
        try {
            let requestPromise = inflightRequests.get(url);
            if (!requestPromise) {
                requestPromise = axios.get(url,{
                    headers:{
                        'Authorization': `Bearer ${token}` 
                    }
                }).then((response) => {
                    if (useMemoryCache) {
                        responseCache.set(url, { data: response.data, ts: Date.now() });
                    }
                    if (usePersistCache) {
                        writePersisted(url, response.data);
                    }
                    return response.data;
                }).catch((err) => {
                    const status = err?.response?.status;
                    if (status === 429) {
                        const retryAfter = Number(err?.response?.headers?.['retry-after']);
                        const cooldownMs = Number.isFinite(retryAfter) && retryAfter > 0
                            ? retryAfter * 1000
                            : DEFAULT_COOLDOWN_MS;
                        cooldownUntil.set(url, Date.now() + cooldownMs);
                    }
                    throw err;
                }).finally(() => {
                    inflightRequests.delete(url);
                });
                inflightRequests.set(url, requestPromise);
            }

            const result = await requestPromise;
            setData(result);
            setError(null);
            setLoading(false);
            return result;
        }catch(error){
            setError(error);
            setLoading(false);
        }
    },[url,token,useMemoryCache,usePersistCache]);

    const refreshHandler = useCallback(() => {
        invalidateFetchCache(url);
        fetchData();
    },[url,fetchData]);

    useEffect(()=>{
        if(parsedAutoLoad && url ){
            fetchData();
            return () => {
                // Cleanup or cancel ongoing requests if needed
            }
        }
    },[url,parsedAutoLoad,fetchData])

    return {data,loading,error,fetchData,refreshHandler}
}

export default useFetch
